// Torneos públicos: lo que se ve sin estar logueado.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  UserWithToken,
  WithData,
  WithMeta,
} from '../api.types';
import { USER_WITH_COSSY, USER_WITH_GAME_IDS } from '../fragments';

export type RoundResultInput = {
  round: number;
  result: 'WIN' | 'LOSS';
};

export const getPublicTournaments = async (
  filters: Record<string, unknown> = {}
) =>
  getGraphQLQueryClient(null).request<WithMeta<Tournament>>(
    gql`
      query publicTournaments($filters: TournamentFilters) {
        data: publicTournaments(filters: $filters) {
          data {
            id
            dateFrom
            dateTo
            title
            tier
            featured
            inscriptionFee

            preInscriptionDateFrom
            preInscriptionDateTo
            preInscriptionFee

            location {
              id
              name
              address
              latitude
              longitude
              zipCode
              city
              googleMapsUrl
              state {
                name
              }
            }

            status
            currentRound
            rounds
            gameTypeId
            tournamentType

            format {
              id
              name
            }

            organizer {
              id
              name
              logoUrl
            }

            organizers {
              id
              organizerId
              isPrimary
              organizer {
                id
                name
                logoUrl
                slug
              }
            }

            isTeamTournament

            acceptsOnlineRegistration
            nicknamesAllowed
            inscriptionDateFrom
            inscriptionDateTo

            players {
              id
              status
              deckName
              user {
                id
                cossyId
              }
            }

            results {
              id
            }

            links {
              type
              url
            }
          }
        }
      }
    `,
    {
      filters,
    }
  );

export const getPublicTournament = async (
  id: number,
  pairings: boolean = false,
  user: Nullable<UserWithToken> = null
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      ${USER_WITH_COSSY}
      ${USER_WITH_GAME_IDS}
      query publicTournament($id: Int!, $pairings: Boolean) {
        data: publicTournament(id: $id, pairings: $pairings) {
          id
          dateFrom
          dateTo
          title
          tier
          status
          ogImage
          gameTypeId
          platform {
            id
            slug
          }
          duelingBookTourneyId
          playedOnDuelingBook
          tournamentType
          withTop
          thirdPlaceMatch
          grandFinalReset
          topType
          description
          acceptsOnlineRegistration
          nicknamesAllowed
          bestOf
          revealAllRounds
          enforceGameReporting
          bountyUserIds

          inscriptionFee
          preInscriptionDateFrom
          preInscriptionDateTo
          preInscriptionFee

          location {
            id
            name
            address
            latitude
            longitude
            zipCode
            city
            googleMapsUrl
            state {
              name
            }
          }

          resultsValidated

          format {
            id
            name
          }

          effectiveBanlist {
            id
            name
            formatId
            logoUrl
            shareHash
            organizerSlug
          }

          organizer {
            id
            name
            logoUrl
          }

          organizers {
            id
            organizerId
            isPrimary
            organizer {
              id
              name
              logoUrl
              slug
            }
          }

          judges {
            id
            userId
            tournamentId
            position
            description
            status
            user {
              id
              name
              secondName
              lastName
              secondLastName
              cossyId
              picture
            }
            judge {
              id
              rc
              pc
              dc
            }
          }

          leagues {
            id
            leagueId
            roundNumber
            league {
              id
              name
            }
          }

          rounds
          currentRound

          roundPlannings {
            id
            tournamentId
            round
            startDate
            endDate
            actualStartDate
            duration
          }

          isTeamTournament
          playersPerTeam

          tournamentTeams {
            id
            name
            position
            wins
            losses
            draws
            points
            dropRound
            players {
              id
              position
              user {
                ...UserWithCossy
              }
            }
          }

          tournamentTeamMatches {
            id
            round
            tableNumber
            status
            team1Id
            team2Id
            winnerTeamId
            team1 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            team2 {
              id
              name
              players {
                id
                position
                deckName
                user {
                  ...UserWithCossy
                }
              }
            }
            winnerTeam {
              id
              name
            }
            individualMatches {
              id
              round
              tableNumber
              matchNumber
              status
              replayUrl
              player1 {
                ...UserWithCossy
                duelingBookId
              }
              player2 {
                ...UserWithCossy
                duelingBookId
              }
              winner {
                ...UserWithCossy
              }
            }
          }

          players {
            id
            status
            deckName
            metaDeckId
            deckImage
            fixedTable

            dropRound

            user {
              ...UserWithGameIds
            }

            results {
              id
              position
              points
              wins
              losses
              draws
              tiebreaker
            }
          }

          matches {
            id
            round
            tableNumber
            isThirdPlace
            bracketSide
            bracketRound
            bracketPosition
            replayUrl
            status
            resultType
            player1 {
              ...UserWithGameIds
              duelingBookId
            }
            player2 {
              ...UserWithGameIds
              duelingBookId
            }
            winner {
              ...UserWithGameIds
            }
            game1Winner {
              id
            }
            game2Winner {
              id
            }
            game3Winner {
              id
            }
            game4Winner {
              id
            }
            game5Winner {
              id
            }
          }

          results {
            id
            position
            points
            wins
            losses
            draws
            tiebreaker
            player {
              id
              user {
                ...UserWithGameIds
              }
            }
          }

          topTournaments {
            id
            title
            status
            tournamentType
            rounds
            currentRound

            players {
              id
              dropRound
              user {
                ...UserWithGameIds
              }
              results {
                id
                position
                points
                wins
                losses
                draws
                tiebreaker
              }
            }

            matches {
              id
              round
              tableNumber
              replayUrl
              status
              player1 {
                ...UserWithCossy
                duelingBookId
              }
              player2 {
                ...UserWithCossy
                duelingBookId
              }
              winner {
                ...UserWithCossy
              }
            }

            results {
              id
              position
              points
              wins
              losses
              draws
              tiebreaker
              player {
                id
                user {
                  ...UserWithCossy
                }
              }
            }
          }
        }
      }
    `,
    {
      id,
      pairings,
    }
  );
